// MACHINERY V2 (Stroje) — the fleet filter sheet's SIX sections (M2.16a).
//
// Split from FleetFilterSheet.tsx so the sheet stays composition + chrome and
// this file is the content. Rendered truth for sections 1-4:
// mobile/screenshots/02-filter-sheet-open.png.
//
// ============================================================
//  WHY SIX, WHEN THE BOARD DRAWS THREE
// ============================================================
// 1 STAV · 2 ZOSKUPIT PODLA · 3 TYP STROJA · 4 LOKACIA · 5 ZOBRAZENIE ·
// 6 ROZSAH. Sections 5 and 6 appear on NO board, and they are not embellishment:
// below 1280 the rail is gone and pattern B absorbs the toolbar, so without them
// a phone or tablet user cannot reach V2 / V3 / V4 or the Vyradene scope AT ALL.
// `fleetView` is localStorage-persisted (M2.13 ruling 3), so a user who landed
// on the V4 wall would be trapped there across reloads. At tablet it is worse
// than a gap: 768-1279 has those controls working TODAY, so shipping the board
// literally would be a REGRESSION rather than a port. THE RULING: the mobile
// pass never removes a working control, it re-homes it. Do not "fix" this back
// to the PNG.
//
// Section 3's manufacturer sub-filter is the same rule plus a data-parity
// obligation — it rides `MachinesQuery.manufacturerId`, the live grid's own
// param, and it is the rail's carry (FilterRail.tsx:16-20).
//
// ============================================================
//  THREE SECTIONS ARE STAGED AND THREE APPLY LIVE — AND THE SHEET SAYS SO
// ============================================================
// STAV / TYP STROJA / LOKACIA stage into the draft and wait for the footer's
// primary. ZOSKUPIT PODLA / ZOBRAZENIE / ROZSAH apply on tap, exactly as the
// desktop toolbar applies them. The seam and the full reasoning are
// useFleetFilterDraft.ts; what this file owes the user is that the difference is
// VISIBLE, because a control sitting above a "Zobrazit n strojov" button
// otherwise reads as waiting for it.
//
// THE IDIOM IS A NOTE ON THE EYEBROW, in the module accent, on the three live
// sections only (SheetSection's `note`). Chosen over reordering the sheet into
// two zones — which would have moved ZOSKUPIT away from the board's position 2,
// and would have put the live zone either directly above the primary button
// (making the adjacency say the opposite of the note) or above STAV, demoting
// the sheet's main filter. The eyebrow is the last thing read before the
// control is tapped, so the label sits where the decision is made. It is drawn
// on no board: the boards draw three sections, none of them live.
//
// ============================================================
//  ALL NINE STATES, NOT THE BOARD'S SIX
// ============================================================
// The board shows six because its mock dataset has six. The vocabulary is
// FLEET_STATE_FILTERS, whose own header says why: ONE table consumed by BOTH
// filter surfaces, "or a state becomes filterable in one place and invisible in
// the other". The rail offers nine; so does this.
//
// STATUS IS TELEMETRY-DERIVED, AND THE VYRADENE SCOPE HAS NO TELEMETRY
// (useFleetPageData.ts:31-37 / :156-158). Scope applies LIVE, so `modes.scope`
// is the APPLIED scope and the test below is the same condition the real
// pipeline uses for `statusFiltersIgnored` — the counts are withheld and the
// page's own note is shown in exactly the case the filters really are ignored,
// never one case wide and never one case narrow. The ticks are left alone: the
// filter is never cleared behind the user's back.
//
// ============================================================
//  ZOBRAZENIE HIDES V2 AT TABLET (M2.16b) — THE ANTI-STRANDING RULE
// ============================================================
// mobile/README.md §V2 makes V2 phone-only: between 768 and 1279 the fleet
// renders the V1 grid for it (FleetContent's `body2AsGrid`). A chip that stays
// selectable there would be a control whose only effect is to light itself up —
// tap "Zoskupené", get the card grid, tap it again, get the card grid. So at
// TABLET the V2 chip is not offered, and a `fleetView` of V2 carried in from
// desktop (it is localStorage-persisted, M2.13 ruling 3) lights the V1 chip
// instead, because at that width V1 IS what V2 renders.
//
// >>> THE STATE IS NOT REWRITTEN. <<< `modes.setView` is never called behind the
// user's back; only the DISPLAY is remapped. Rotate back past 1280 and the
// grouped rows are still what the user chose. This is the same discipline
// FleetPage keeps for the wall's forced `active` scope: derive, never mutate.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, V2_PHONE, useV2MediaQuery } from "../useV2MediaQuery";
import { MachineryV2FleetView } from "../machineryV2UiSlice";
import { SheetCheckRow, SheetChip, SheetChipRow, SheetSection } from "../mobile/sheetAtoms";
import StatusIcon from "./StatusIcon";
import { FleetPageData } from "./fleetPageData";
import { FLEET_GROUP_BY_OPTIONS } from "./fleetGroupBy";
import { FLEET_STATE_FILTERS, filterInk } from "./fleetStatusFilters";
import { derivedManufacturers } from "./fleetSelectors";
import { FleetFilterDraftApi } from "./useFleetFilterDraft";

const VIEW_OPTIONS: { key: MachineryV2FleetView; labelKey: string }[] = [
  { key: "V1", labelKey: "myMachinery:v2.fleet.viewBigCards" },
  { key: "V2", labelKey: "myMachinery:v2.fleet.viewGrouped" },
  { key: "V3", labelKey: "myMachinery:v2.fleet.viewTable" },
  { key: "V4", labelKey: "myMachinery:v2.fleet.viewWall" },
];

export interface FleetSheetSectionsProps {
  api: FleetFilterDraftApi;
  data: FleetPageData;
}

const FleetSheetSections: React.FC<FleetSheetSectionsProps> = ({ api, data }) => {
  const { translate } = useTranslation("myMachinery");
  // F-A: the tablet tier is the module's two queries composed, not a third
  // reader — below desktop AND not phone. Identical to the shared hook's
  // `isTablet` at every integer width; correct in the fractional bands too.
  //
  // BOTH READS ARE UNCONDITIONAL, ON THEIR OWN LINES. Composing them inline
  // with `&&` short-circuits the second call the moment the first is false —
  // a hook that runs on some renders and not others, which is the
  // rules-of-hooks violation eslint rejected while this fix was written.
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);
  const phone = useV2MediaQuery(V2_PHONE);
  const isTablet = belowDesktop && !phone;
  const { draft, modes } = api;
  // See the ZOBRAZENIE block in this file's header.
  const viewOptions = isTablet ? VIEW_OPTIONS.filter((o) => o.key !== "V2") : VIEW_OPTIONS;
  const shownView = isTablet && modes.view === "V2" ? "V1" : modes.view;
  // THE APPLIED SCOPE, because there is no other kind — see the header.
  const statusCountsApply = modes.scope === "active";
  const liveNote = translate("myMachinery:v2.mobile.appliesNow");

  // DERIVED FROM THE DRAFT TYPE, NOT `data.manufacturers`. That map is built
  // from the APPLIED `query.typeId` (useFleetPageData.ts:220-223), so it would
  // list the previous type's manufacturers while the user looked at a new one.
  // Same pure function, pending input — and correct because TYPE is staged.
  const manufacturers = derivedManufacturers(data.flat, draft.typeId);

  return (
    <>
      <SheetSection title={translate("myMachinery:v2.fleet.status")} divider={false}>
        {!statusCountsApply && (
          <div style={{ fontSize: 11.5, color: "var(--muted)", padding: "2px 0 6px" }}>
            {translate("myMachinery:v2.fleet.statusFiltersIgnored")}
          </div>
        )}
        {FLEET_STATE_FILTERS.map((s) => (
          <SheetCheckRow
            key={s.state}
            label={translate(s.labelKey)}
            count={statusCountsApply ? data.stateCounts[s.state] || 0 : undefined}
            checked={draft.states.indexOf(s.state) >= 0}
            icon={s.icon ? <StatusIcon kind={s.icon} size={12} /> : undefined}
            iconInk={filterInk(s.token)}
            onToggle={() => api.toggleState(s.state)}
          />
        ))}
      </SheetSection>

      <SheetSection title={translate("myMachinery:v2.fleet.groupBy")} note={liveNote}>
        <SheetChipRow>
          {FLEET_GROUP_BY_OPTIONS.map((o) => (
            <SheetChip
              key={o.key}
              label={translate(o.labelKey)}
              active={modes.groupBy === o.key}
              onClick={() => modes.setGroupBy(o.key)}
            />
          ))}
        </SheetChipRow>
      </SheetSection>

      <SheetSection title={translate("myMachinery:machineType")}>
        <SheetCheckRow
          label={translate("myMachinery:v2.fleet.allTypes")}
          count={data.flat.length}
          checked={!draft.typeId}
          onToggle={() => api.selectType(undefined)}
        />
        {data.types.map((t) => (
          <React.Fragment key={t.id}>
            <SheetCheckRow
              label={t.name}
              count={data.typeCounts[t.name] || 0}
              checked={draft.typeId === t.id}
              onToggle={() => api.selectType(draft.typeId === t.id ? undefined : t.id)}
            />
            {draft.typeId === t.id &&
              manufacturers.map((m) => (
                <SheetCheckRow
                  key={m.id}
                  label={m.name}
                  depth={1}
                  checked={draft.manufacturerId === m.id}
                  onToggle={() =>
                    api.selectManufacturer(draft.manufacturerId === m.id ? undefined : m.id)
                  }
                />
              ))}
          </React.Fragment>
        ))}
      </SheetSection>

      {data.locations.length > 0 && (
        <SheetSection title={translate("myMachinery:locationFilter")}>
          {data.locations.map((node) => (
            <React.Fragment key={node.id}>
              <SheetCheckRow
                label={node.name}
                count={node.count}
                checked={draft.location === node.id}
                onToggle={() =>
                  api.selectLocation(draft.location === node.id ? null : node.id)
                }
              />
              {node.children.map((child) => (
                <SheetCheckRow
                  key={child.id}
                  label={child.name}
                  count={child.count}
                  depth={1}
                  checked={draft.location === child.id}
                  onToggle={() =>
                    api.selectLocation(draft.location === child.id ? null : child.id)
                  }
                />
              ))}
            </React.Fragment>
          ))}
        </SheetSection>
      )}

      <SheetSection title={translate("myMachinery:v2.mobile.view")} note={liveNote}>
        <SheetChipRow>
          {viewOptions.map((o) => (
            <SheetChip
              key={o.key}
              label={translate(o.labelKey)}
              active={shownView === o.key}
              onClick={() => modes.setView(o.key)}
            />
          ))}
        </SheetChipRow>
      </SheetSection>

      <SheetSection title={translate("myMachinery:v2.mobile.scope")} note={liveNote}>
        <SheetChipRow>
          <SheetChip
            label={translate("myMachinery:activeView")}
            count={data.activeCount}
            active={modes.scope === "active"}
            onClick={() => modes.setScope("active")}
          />
          <SheetChip
            label={translate("myMachinery:decommissionedView")}
            count={data.decommissionedCount}
            active={modes.scope === "decommissioned"}
            onClick={() => modes.setScope("decommissioned")}
          />
        </SheetChipRow>
      </SheetSection>
    </>
  );
};

export default FleetSheetSections;
